import {
  addDays,
  addHours,
  differenceInCalendarDays,
  differenceInMinutes,
  isAfter,
  isBefore,
  parseISO,
} from 'date-fns';
import { ReservationStatus } from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';

export const DETAIL_TAB = {
  INSIGHTS: 'insights',
  ROSTER: 'roster',
};

export const ROSTER_DEFAULT_WINDOW_MINUTES = 60;

export function getDefaultDetailTab(schedule, now = new Date()) {
  const { isInProgress, isInThePast, startDateTime } = schedule;
  const startsSoon = differenceInMinutes(startDateTime, now) < ROSTER_DEFAULT_WINDOW_MINUTES;

  return isInProgress || isInThePast || startsSoon ? DETAIL_TAB.ROSTER : DETAIL_TAB.INSIGHTS;
}

export const LAST_WEEK_FILL_MAX_AGE_DAYS = 8;

export function getMostRecentFillHistoryEntry(history = [], scheduleStartDateTime) {
  if (!scheduleStartDateTime) {
    return null;
  }

  const entry = [...history]
    .filter(({ scheduleDate } = {}) => scheduleDate)
    .sort((a, b) => parseISO(b.scheduleDate) - parseISO(a.scheduleDate))
    .find(({ scheduleDate }) => {
      const daysBeforeSchedule = differenceInCalendarDays(scheduleStartDateTime, parseISO(scheduleDate));
      return daysBeforeSchedule >= 1 && daysBeforeSchedule <= LAST_WEEK_FILL_MAX_AGE_DAYS;
    });

  return entry && entry.totalReservations != null && entry.maxCapacity != null ? entry : null;
}

export const ACTIONS_WINDOW_HOURS_AFTER_END = 6;
export const EXTENDED_ACTIONS_WINDOW_DAYS_AFTER_END = 14;

export function getActionWindows(schedule, now = new Date()) {
  const { endDateTime } = schedule;
  const actionsWindowEnd = addHours(endDateTime, ACTIONS_WINDOW_HOURS_AFTER_END);
  const extendedActionsWindowEnd = addDays(endDateTime, EXTENDED_ACTIONS_WINDOW_DAYS_AFTER_END);

  return {
    canPerformScheduleActions: isBefore(now, actionsWindowEnd),
    isInExtendedActionsWindow: isAfter(now, actionsWindowEnd) && isBefore(now, extendedActionsWindowEnd),
  };
}

const COUNTABLE_RESERVATION_STATUSES = [ReservationStatus.PROCESSED, ReservationStatus.QUEUED];

export function countReservations(roster = []) {
  return roster.reduce(
    (counts, { reservationStatus, visitNumberToVenue }) => {
      const isCountable = COUNTABLE_RESERVATION_STATUSES.includes(reservationStatus);

      return {
        newReservationsCount: counts.newReservationsCount + (isCountable && visitNumberToVenue === 1 ? 1 : 0),
        reservationsCount: counts.reservationsCount + (isCountable ? 1 : 0),
      };
    },
    { newReservationsCount: 0, reservationsCount: 0 }
  );
}

export const ADD_SPOT_ACTION = {
  ADD_SPOT: 'addSpot',
  OPPORTUNITY: 'opportunity',
};

export function getAddSpotAction(schedule, { hasSpotManagePermission = false, opportunityActedOn = false } = {}) {
  const {
    classpassSpotsUnfilled,
    hasIntegrationData,
    isFull,
    isInThePast,
    isLocked,
    opportunity,
    reservationsStopped,
    smartspotEnabled,
  } = schedule;

  if (!hasSpotManagePermission || isInThePast || isLocked || reservationsStopped) {
    return null;
  }

  const recommendation = opportunity ? opportunity.recommendation : false;
  if (recommendation && !smartspotEnabled && !opportunityActedOn) {
    return { recommendation, type: ADD_SPOT_ACTION.OPPORTUNITY };
  }

  if (smartspotEnabled) {
    return (classpassSpotsUnfilled === 0 || classpassSpotsUnfilled === Infinity) && !isFull
      ? { type: ADD_SPOT_ACTION.ADD_SPOT }
      : null;
  }

  if (!hasIntegrationData) {
    return { type: ADD_SPOT_ACTION.ADD_SPOT };
  }

  return isFull ? null : { type: ADD_SPOT_ACTION.ADD_SPOT };
}
